import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";

const Account = () => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user === false) navigate("/login");
    if (user) api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, [user, navigate]);

  if (!user) return <div className="min-h-[60vh]" data-testid="account-loading" />;

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div data-testid="account-page" className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <Reveal>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-champagne mb-4">{t("account.title")}</p>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-charcoal">
              {t("account.welcome")}, {user.name}
            </h1>
            <p className="text-stone-500 mt-3">{user.email}</p>
          </div>
          <Button
            variant="outline"
            onClick={doLogout}
            data-testid="logout-button"
            className="rounded-none border-stone-300 text-xs uppercase tracking-[0.18em] hover:border-charcoal"
          >
            {t("account.logout")}
          </Button>
        </div>
      </Reveal>

      <div className="mt-16">
        <h2 className="font-serif text-2xl text-charcoal border-b border-stone-200 pb-4">{t("account.orders")}</h2>
        {orders.length === 0 ? (
          <p className="text-stone-400 py-10" data-testid="no-orders">{t("account.noOrders")}</p>
        ) : (
          <div className="mt-6 space-y-4">
            {orders.map((o, i) => (
              <div key={i} data-testid={`order-${i}`} className="bg-white border border-stone-200 p-6">
                <div className="flex justify-between items-center">
                  <p className="text-xs uppercase tracking-widest text-stone-400">
                    {new Date(o.created_at).toLocaleDateString()}
                  </p>
                  <p className="text-charcoal font-medium">{formatPrice(o.amount)}</p>
                </div>
                <ul className="mt-3 text-sm text-stone-600 space-y-1">
                  {(o.items || []).map((it, j) => (
                    <li key={j}>
                      {it.quantity} × {it.name} <span className="text-stone-400">({it.format})</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Account;
